import { CustomTable, Button, OptionType, Pagination, useTitle, TableType } from "modules/common";
import { ReactComponent as DetailsIcon } from "assets/icons/details.svg";
import { useContext, useMemo, useState } from "react";
import { OuterModuleDetailsVersionModal, useOuterModuleVersions } from "modules/outerModules";
import { GetOuterModuleVersionRequest, OuterModuleVersion } from "services";
import { ValueType } from "react-select";
import { Link } from "react-router-dom";
import { AppRoutes } from "routing";
import { DEFAULT_PAGE_SIZE_OPTION } from "helpers/PaginationHelper";
import { AccessibilityContext } from "./AccessibilityContext";

const defaultTableColumns: any[] = [
    {
        Header: "VERZIJA",
        accessor: "version",
        disableSortBy: true,
        width: 2,
    },
    {
        Header: "OPIS",
        accessor: "description",
        disableSortBy: true,
        width: 10,
    },
    {
        Header: "DATUM",
        accessor: "created",
        disableSortBy: true,
        width: 4,
        Cell: ({ row }: any) => {
            const { original } = row;
            return original?.created && new Date(original.created).toLocaleString("HR-hr");
        },
    },
];

const initialRequest: GetOuterModuleVersionRequest = {
    page: 1,
    pageSize: Number(DEFAULT_PAGE_SIZE_OPTION.value),
};

export const ModuleVersionsAdministration = () => {
    useTitle("Verzije modula - eSavjetnik");
    const [pageSize, setPageSize] = useState<OptionType>(DEFAULT_PAGE_SIZE_OPTION);
    const [request, setRequest] = useState<GetOuterModuleVersionRequest>(initialRequest); // this should be diractly called in query so useState can be eliminated
    const [selectedVersion, setSelectedVersion] = useState<OuterModuleVersion>();
    const [isModalOpen, setIsModalOpen] = useState(false);

    const { data: outerModuleVersions, isLoading } = useOuterModuleVersions(request);

    const tableColumns = useMemo(
        () => [
            ...defaultTableColumns,
            {
                id: "info",
                disableSortBy: true,
                width: 1,
                className: "details-icon-cell",
                Cell: ({ row }: any) => {
                    return (
                        <DetailsIcon
                            className="details-icon"
                            onClick={() => {
                                setIsModalOpen(true);
                                setSelectedVersion(row.original);
                            }}
                        />
                    );
                },
            },
        ],
        [defaultTableColumns]
    );

    const handlePageSizeChange = (value: ValueType<OptionType, false>) => {
        value && setPageSize(value);
        setRequest(prev => ({
            ...prev,
            page: 1,
            pageSize: Number(value?.value),
        }));
    };

    const handlePageClick = (event: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
        setRequest(prev => {
            const target = event.currentTarget ?? event.target;
            return { ...prev, page: Number(parseInt(target.id)) };
        });
    };

    const handleNexPageClick = () => {
        setRequest(prev => ({ ...prev, page: prev.page + 1 }));
    };

    const handlePrevPageClick = () => {
        setRequest(prev => ({ ...prev, page: prev.page - 1 }));
    };

    const handlePageSelect = (page: number) => {
        setRequest(prev => ({ ...prev, page }));
    };

    const handleFirstPageClick = () => {
        setRequest(prev => ({ ...prev, page: 1 }));
    };

    const handleLastPageClick = () => {
        setRequest(prev => ({
            ...prev,
            page: Math.ceil(
                (outerModuleVersions?.total ?? 0) /
                    parseInt(pageSize?.value ?? DEFAULT_PAGE_SIZE_OPTION.value)
            ),
        }));
    };

    const handleModalClose = () => {
        setIsModalOpen(false);
        setSelectedVersion(undefined);
    };
    const { lowVision } = useContext(AccessibilityContext);
    return (
        <div className="module-versions-administration">
            <div className="f">
                <h1 className="module-versions-administration__heading mb-28">
                    Administracija verzija modula
                </h1>
                <Link className="f-a-x-r" to={AppRoutes.ModulesAdministration}>
                    <Button size="xsmall" variant="primary-ghost" className={lowVision ? "btn--low--vision" : undefined}>  
                        Dodatni moduli
                    </Button>
                </Link>
            </div>
            <div className="table-container">
                <CustomTable
                    flex
                    data={outerModuleVersions?.items ?? []}
                    columns={tableColumns}
                    loading={isLoading}
                    tableType={TableType.AdministrationTable}
                />
            </div>
            {outerModuleVersions && outerModuleVersions.items.length > 0 && (
                <Pagination
                    className="mt-24"
                    currentPage={request.page}
                    numberOfPages={Math.ceil(outerModuleVersions.total / parseInt(pageSize.value))}
                    pageNeighbours={1}
                    pageSize={pageSize}
                    onPageSizeChange={handlePageSizeChange}
                    handlePageClick={handlePageClick}
                    handlePageSelect={handlePageSelect}
                    next={handleNexPageClick}
                    prev={handlePrevPageClick}
                    first={handleFirstPageClick}
                    last={handleLastPageClick}
                />
            )}

            <OuterModuleDetailsVersionModal
                isOpen={isModalOpen}
                handleClose={handleModalClose}
                moduleId={selectedVersion?.id}
            />
        </div>
    );
};
